/**
 * Breakdown menu: multi-screen menu to select event+prop pair
 */
import {renameEvent} from 'common/report/util/events';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {propagateEvent} from 'common/util/dom';
import {PATH_SEPARATOR} from 'common/constants';
import {truncateMiddle} from 'common/util/string';
import {getSelectedPropertyName} from 'common/query-builder/query-block/utils/property-util';
import {Segmentation} from 'common/types/reports/query';

import {ChildMenu} from '../child-menu';
import type {EventItem} from '../events-menu/types';
import type {PropertySectionItem} from '../properties-menu/item-menu-types';
import type {EventsMenu} from '../events-menu';
import type {PropertiesMenu} from '../properties-menu';
import {MAX_LABEL_LENGTH} from '../constants';
import {IncludeCustomPropertyType} from '../properties-menu/types';
import {isListOfObjectsSegment} from '../segment-menu/types';
import {propertySelectionToSegmentation} from '../segment-menu/util';
import {template} from './template';

interface SelectedEventProperty {
  event: EventItem;
  property: PropertySectionItem;
}

interface SelectedEventPropertyItem {
  item: SelectedEventProperty;
}

export type SelectEventPropertyEvent = CustomEvent<SelectedEventPropertyItem>;

interface BreakdownMenuState {
  staged: SelectedEventProperty;
}

interface BreakdownMenuParams {
  listProperty?: Segmentation;
}

interface BreakdownMenuAttrs {
  'include-custom-properties': IncludeCustomPropertyType;
}

export class BreakdownMenu extends ChildMenu<
  BreakdownMenuState,
  BreakdownMenuAttrs,
  SelectedEventProperty,
  BreakdownMenuParams
> {
  protected childMenu: EventsMenu | PropertiesMenu;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
        description: `whether to hide custom properties from properties menus`,
      },
    };
  }

  get config() {
    return {
      template,

      defaultState: {
        staged: null,
      },
      params: {
        listProperty: Object,
      },

      helpers: {
        ...super.config.helpers,
        propagateEvent: (ev) => propagateEvent(this, ev),
        handleSelectProperty: (ev) => {
          let property = ev.detail.item;
          const segmentation = propertySelectionToSegmentation(property) as Segmentation;
          const currSegmentation =
            this.state.staged.property && (propertySelectionToSegmentation(this.state.staged.property) as Segmentation);

          if (currSegmentation && isListOfObjectsSegment(currSegmentation)) {
            property = {...this.state.staged.property, listItemGroup: segmentation};
          }

          // don't emit event if we still need to pick a prop for the list of objects
          if (!isListOfObjectsSegment(segmentation) || property?.listItemGroup) {
            this.dispatchEvent(
              new CustomEvent<SelectedEventPropertyItem>(`select`, {
                detail: {
                  item: {
                    event: this.getSelectedEvent(),
                    property,
                  },
                },
              }),
            );
          }
          this.update({staged: {...this.state.staged, property}});
        },
      },
    };
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `selected` || (attr === `open` && !this.attr(`open`))) {
      this.update({staged: this.attr(`selected`)});
    }
  }

  handleSelectEvent(event: EventItem) {
    this.update({staged: {event}, searchFilter: ``});
  }

  public getSelectedProperty() {
    return this.state.staged?.property || null;
  }

  getSelectedHoverTooltipData() {
    return this.childMenu?.getSelectedHoverTooltipData();
  }

  handleTooltipContextAction(ev: Event) {
    return this.childMenu?.handleTooltipContextAction(ev);
  }

  public getSelectedEvent() {
    // depending on whether this is one part of a compound menu
    let selected = this.state.staged?.event;
    if (selected && selected.event) {
      selected = selected.event;
    }
    return selected;
  }

  public getSelectedEventName() {
    const selected = this.getSelectedEvent();
    return selected && (selected.custom ? selected.name || selected.label : selected.value);
  }

  getPrefixLabel() {
    let label: string = null;
    const selected = this.getSelectedEvent();
    const eventName = this.getSelectedEventName();
    let evName = renameEvent(eventName);

    if (evName) {
      /** @todo [pablo] find a better workaround for Insights using properties-menu for people mode */
      if (selected.value === `$all_people`) {
        evName = selected.label ?? eventName;
      }

      label = truncateMiddle(evName, MAX_LABEL_LENGTH) + PATH_SEPARATOR; // event name + right arrow
    }

    return label;
  }

  selectedLabel() {
    let label = ``;
    const propName = getSelectedPropertyName({
      selected: this.getSelectedProperty(),
      showSpecialDatePropertyUnit: this.attr(`show-special-date-property-unit`),
    });

    if (!this.getSelectedEvent()?.value) {
      label = `Select event...`;
    } else {
      label = propName || `Select property...`;
    }

    return truncateMiddle(label, MAX_LABEL_LENGTH);
  }
}

export const BreakdownMenuEl = defineAndMakeJsxFactory(`mp-breakdown-menu`, BreakdownMenu);
export const BreakdownMenuReact = panel2react(`mp-breakdown-menu`, BreakdownMenu);
